import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

export interface SupportTicket {
  id: string;
  channel_id: string | null;
  channel_name: string | null;
  customer_name: string;
  customer_email: string | null;
  subject: string;
  body: string;
  status: 'open' | 'answered' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  source: 'manual' | 'mercado_livre' | 'shopee' | 'whatsapp' | 'email';
  reply: string | null;
  replied_at: string | null;
  created_at: string;
}

export interface TicketInput {
  channel_id?: string | null;
  customer_name: string;
  customer_email?: string;
  subject: string;
  body: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  source?: 'manual' | 'mercado_livre' | 'shopee' | 'whatsapp' | 'email';
}

function translateTicketError(msg: string): string {
  if (msg.includes('duplicate') || msg.includes('unique'))
    return 'Já existe um ticket com esses dados.';
  if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('new row violates'))
    return 'Permissão insuficiente para gerenciar tickets.';
  if (msg.includes('foreign key') || msg.includes('violates foreign key'))
    return 'Canal inválido. Recarregue a página e tente novamente.';
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('Failed to fetch'))
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  return 'Erro ao salvar ticket. Verifique sua conexão e tente novamente.';
}

export function useSupportTickets() {
  const { tenantId } = useTenant();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);
    const { data, error: err } = await supabase
      .from('support_tickets')
      .select(`
        id, channel_id, customer_name, customer_email,
        subject, body, status, priority, source,
        reply, replied_at, created_at,
        channels(name)
      `)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    if (err) { setFetchError(translateTicketError(err.message)); setLoading(false); return; }
    const rows = (data ?? []).map((r: any) => ({
      ...r,
      channel_name: r.channels?.name ?? null,
    })) as SupportTicket[];
    setTickets(rows);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  async function addTicket(input: TicketInput): Promise<string> {
    if (!tenantId) throw new Error('Empresa não encontrada. Faça login novamente.');
    const { data, error: err } = await (supabase as any)
      .from('support_tickets')
      .insert({ ...input, tenant_id: tenantId })
      .select('id')
      .single();
    if (err) throw new Error(translateTicketError(err.message));

    // Encaminha o conteúdo do ticket ao suporte por e-mail (best-effort,
    // mesmo mecanismo da denúncia de farm — não bloqueia a criação)
    void supabase.functions.invoke('support-email', {
      body: {
        subject: `Ticket de atendimento: ${(input as any).subject ?? 'sem assunto'}`,
        lines: [
          `Novo ticket aberto na aba de Atendimento.`,
          '',
          `Protocolo: ${(data as { id: string }).id}`,
          `Cliente: ${(input as any).customer_name ?? '—'} (${(input as any).customer_email ?? '—'})`,
          `Prioridade: ${(input as any).priority ?? '—'} · Origem: ${(input as any).source ?? '—'}`,
          '',
          'Conteúdo:',
          String((input as any).body ?? ''),
        ],
      },
    }).catch(() => { /* e-mail é best-effort */ });

    await load();
    return (data as { id: string }).id;
  }

  async function replyTicket(id: string, reply: string): Promise<void> {
    const { error: err } = await (supabase as any)
      .from('support_tickets')
      .update({ reply, replied_at: new Date().toISOString(), status: 'answered', updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) throw new Error(translateTicketError(err.message));
    setTickets((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, reply, status: 'answered', replied_at: new Date().toISOString() } : t
      )
    );
  }

  async function changeStatus(id: string, status: SupportTicket['status']): Promise<void> {
    const { error: err } = await (supabase as any)
      .from('support_tickets')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) throw new Error(translateTicketError(err.message));
    setTickets((prev) => prev.map((t) => t.id === id ? { ...t, status } : t));
  }

  return { tickets, loading, fetchError, reload: load, addTicket, replyTicket, changeStatus };
}
